import moment from 'moment';
import Swal from 'sweetalert2';
import HelperMixin from '../../Shared/HelperMixin.vue';

const STATUS_OPEN = 1;
const STATUS_SUBMITTED = 2;
const STATUS_FINALIZED = 3;
const STATUS_CANCELLED = 4;

export default {
    name: 'InventoryCountDetails',
    mixins: [HelperMixin],

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails || !this.canViewInventoryCountsUi()) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'ليس لديك صلاحية للوصول إلى تفاصيل الجرد');
            this.$router.push('/Home');
            return;
        }

        const id = Number(this.$route.params.id);
        if (!id) {
            this.$router.push('/InventoryCount');
            return;
        }

        await this.LoadDetail(id);
    },

    mounted() {
        this.MoveBootstrapModalsToBody();
    },

    beforeDestroy() {
        // مودال إضافة صنف فقط (لوحة المراجعة أصبحت inline داخل الصفحة).
        const el = this.$refs.addLineModalEl;
        if (el) {
            const inst = window.bootstrap && window.bootstrap.Modal && window.bootstrap.Modal.getInstance(el);
            if (inst) inst.hide();
            if (el.parentNode === document.body) {
                document.body.removeChild(el);
            }
        }
        if (this.productSearchTimer) clearTimeout(this.productSearchTimer);
    },

    watch: {
        '$route.params.id': {
            handler(newId, oldId) {
                if (!newId || newId === oldId) return;
                this.LoadDetail(Number(newId));
            },
        },
    },

    computed: {
        currentStep() {
            const st = this.detail && this.detail.status;
            if (st === STATUS_OPEN) return 2;
            if (st === STATUS_SUBMITTED) return 3;
            if (st === STATUS_FINALIZED) return 4;
            if (st === STATUS_CANCELLED) return 0;
            return 1;
        },
        filteredLines() {
            const q = (this.linesSearch || '').trim().toLowerCase();
            if (!q) return this.detailLines;
            return this.detailLines.filter((ln) => {
                const txt = [
                    ln.productName,
                    ln.batchNumber,
                    ln.tradeName,
                    ln.nationalCode,
                    ln.scientificName,
                    ln.countryOfOrigin,
                    ln.packageType,
                ].filter(Boolean).join(' ').toLowerCase();
                return txt.indexOf(q) !== -1;
            });
        },
        differenceLines() {
            return this.detailLines.filter((ln) => !ln.isCancelled
                && ln.actualQty !== null && ln.actualQty !== undefined
                && Number(ln.actualQty) !== Number(ln.systemQty));
        },
        filteredDifferenceLines() {
            const q = (this.finalizeSearch || '').trim().toLowerCase();
            if (!q) return this.differenceLines;
            return this.differenceLines.filter((ln) => {
                const txt = [
                    ln.productName,
                    ln.batchNumber,
                    ln.tradeName,
                    ln.nationalCode,
                    ln.scientificName,
                ].filter(Boolean).join(' ').toLowerCase();
                return txt.indexOf(q) !== -1;
            });
        },
        acceptedCount() {
            return this.differenceLines.filter((ln) => this.finalizeDecisions[ln.id] === true).length;
        },
        rejectedCount() {
            return this.differenceLines.filter((ln) => this.finalizeDecisions[ln.id] === false).length;
        },
        totalSystemQty() {
            return this.detailLines
                .filter((ln) => !ln.isCancelled)
                .reduce((s, ln) => s + Number(ln.systemQty || 0), 0);
        },
        totalActualQty() {
            return this.detailLines
                .filter((ln) => !ln.isCancelled)
                .reduce((s, ln) => s + Number(this.lineActualQty(ln) || 0), 0);
        },
        totalDifference() {
            return this.totalActualQty - this.totalSystemQty;
        },
        addLineRequiresExpiry() {
            return !!(this.selectedProduct && this.selectedProduct.requiresExpiryDate);
        },
    },

    filters: {
        moment(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY HH:mm');
        },
        momentDate(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY');
        },
    },

    data() {
        return {
            detail: null,
            detailLines: [],
            detailLoading: false,
            lineEntries: {},
            linesSearch: '',

            // Finalize review (inline panel — لا تستخدم مودال لاستيعاب أعداد كبيرة من الأصناف)
            finalizeReviewOpen: false,
            finalizeDecisions: {},
            finalizeNote: '',
            createAdjustmentVouchers: true,
            finalizeSubmitting: false,
            finalizeSearch: '',

            // Add Line modal
            addLineSubmitting: false,
            addLineForm: {
                ProductsId: null,
                BatchNumber: '',
                ExpiryDate: null,
                PackageType: '',
                CountryOfOrigin: '',
                ActualQty: 1,
                Note: '',
            },
            addLineErrors: {},
            addLineProductLoading: false,
            selectedProduct: null,
            selectedProductPackagings: [],

            productOptions: [],
            productSearchQuery: '',
            countryOptions: [],
            lookupLoading: {
                products: false,
                countries: false,
            },
            productSearchTimer: null,
        };
    },

    methods: {
        goBack() {
            this.$router.push({ name: 'InventoryCount' });
        },

        statusBadgeClass(badge) {
            return 'inventory-status-badge inventory-status-badge--' + (badge || 'secondary');
        },

        LoadDetail(id) {
            this.detailLoading = true;
            return this.$http.GetInventoryCountById(id)
                .then((res) => {
                    this.detailLoading = false;
                    const info = (res && res.data && res.data.info) || null;
                    this.detail = info;
                    this.detailLines = (info && info.lines) || [];
                    this.lineEntries = {};
                    this.detailLines.forEach((ln) => {
                        if (!ln.isCancelled) {
                            this.$set(this.lineEntries, ln.id, {
                                actualQty: ln.actualQty == null ? '' : Number(ln.actualQty),
                                note: ln.note || '',
                            });
                        }
                    });
                })
                .catch(() => {
                    this.detailLoading = false;
                    this.$helper.ShowMessage('error', 'تعذّر التحميل', 'فشل جلب تفاصيل الجرد.');
                });
        },

        lineActualQty(ln) {
            const e = this.lineEntries[ln.id];
            if (e && e.actualQty !== '' && e.actualQty !== null && e.actualQty !== undefined && !isNaN(Number(e.actualQty))) {
                return Number(e.actualQty);
            }
            if (ln.actualQty != null) return Number(ln.actualQty);
            return null;
        },

        lineDifference(ln) {
            const actual = this.lineActualQty(ln);
            if (actual === null) return null;
            return actual - Number(ln.systemQty || 0);
        },

        diffClass(diff) {
            if (diff === null || diff === undefined) return 'diff-zero';
            if (diff > 0) return 'diff-positive';
            if (diff < 0) return 'diff-negative';
            return 'diff-zero';
        },

        diffDisplay(diff) {
            if (diff === null || diff === undefined) return '—';
            if (diff > 0) return '+' + diff;
            return String(diff);
        },

        buildLinesPayload() {
            const arr = [];
            Object.keys(this.lineEntries).forEach((id) => {
                const e = this.lineEntries[id];
                if (!e) return;
                const v = e.actualQty;
                if (v === '' || v === null || v === undefined || isNaN(Number(v))) return;
                arr.push({
                    LineId: Number(id),
                    ActualQty: Number(v),
                    Note: e.note || null,
                });
            });
            return arr;
        },

        SaveLines() {
            if (!this.detail) return;
            const lines = this.buildLinesPayload();
            if (lines.length === 0) {
                this.$helper.ShowMessage('warning', 'لا توجد إدخالات', 'الرجاء إدخال الكميات الفعلية على الأقل لسطر واحد.');
                return;
            }
            this.$blockUI.Start();
            this.$http.SaveInventoryCountLines(this.detail.id, { Lines: lines })
                .then((res) => {
                    this.$blockUI.Stop();
                    const msg = (res && res.data && (res.data.message || 'تم الحفظ بنجاح')) || 'تم الحفظ بنجاح';
                    this.$helper.ShowMessage('success', 'تم الحفظ', msg);
                    this.LoadDetail(this.detail.id);
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = this.ExtractErrorMessage(err, 'تعذّر الحفظ.');
                    this.$helper.ShowMessage('error', 'فشل الحفظ', msg);
                });
        },

        SubmitForReview() {
            if (!this.detail) return;
            Swal.fire({
                title: 'تأكيد الإرسال للمراجعة',
                text: 'سيتم إرسال الجرد للمراجعة. لن يمكن تعديل الكميات بعد ذلك إلا بإلغاء الجرد.',
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-primary me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد الإرسال',
                cancelButtonText: 'إلغاء',
            }).then((result) => {
                if (!result.isConfirmed) return;
                const lines = this.buildLinesPayload();
                this.$blockUI.Start();
                this.$http.SubmitInventoryCountSession(this.detail.id, { Lines: lines })
                    .then((res) => {
                        this.$blockUI.Stop();
                        const msg = (res && res.data && (res.data.message || 'تم الإرسال للمراجعة')) || 'تم الإرسال للمراجعة';
                        this.$helper.ShowMessage('success', 'إرسال ناجح', msg);
                        this.LoadDetail(this.detail.id);
                    })
                    .catch((err) => {
                        this.$blockUI.Stop();
                        const msg = this.ExtractErrorMessage(err, 'تعذّر الإرسال.');
                        this.$helper.ShowMessage('error', 'فشل الإرسال', msg);
                    });
            });
        },

        // ─── Finalize Review Modal ────────────────────────────────────────

        OpenFinalizeReview() {
            if (!this.detail) return;
            this.finalizeDecisions = {};
            this.differenceLines.forEach((ln) => {
                this.$set(this.finalizeDecisions, ln.id, true);
            });
            this.finalizeNote = '';
            this.createAdjustmentVouchers = true;
            this.finalizeSearch = '';
            this.finalizeReviewOpen = true;
            this.$nextTick(() => {
                const el = this.$refs.finalizeReviewPanelEl;
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            });
        },

        CloseFinalizeReview() {
            if (this.finalizeSubmitting) return;
            this.finalizeReviewOpen = false;
            this.finalizeDecisions = {};
            this.finalizeNote = '';
            this.finalizeSearch = '';
        },

        SetAllDecisions(accept) {
            if (this.finalizeSubmitting) return;
            this.differenceLines.forEach((ln) => {
                this.$set(this.finalizeDecisions, ln.id, !!accept);
            });
        },

        ConfirmFinalize() {
            if (!this.detail) return;
            const decisions = Object.keys(this.finalizeDecisions).map((id) => ({
                LineId: Number(id),
                Accept: !!this.finalizeDecisions[id],
            }));
            this.finalizeSubmitting = true;
            this.$blockUI.Start();
            this.$http.FinalizeInventoryCountSession(this.detail.id, {
                Note: this.finalizeNote || null,
                Decisions: decisions,
                CreateAdjustmentVouchers: this.createAdjustmentVouchers,
            })
                .then((res) => {
                    this.$blockUI.Stop();
                    this.finalizeSubmitting = false;
                    const msg = (res && res.data && (res.data.message || 'تم الترحيل بنجاح')) || 'تم الترحيل بنجاح';
                    this.$helper.ShowMessage('success', 'ترحيل ناجح', msg);
                    this.finalizeReviewOpen = false;
                    this.LoadDetail(this.detail.id);
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.finalizeSubmitting = false;
                    const msg = this.ExtractErrorMessage(err, 'تعذّر الترحيل.');
                    this.$helper.ShowMessage('error', 'فشل الترحيل', msg);
                });
        },

        CancelSession() {
            if (!this.detail) return;
            if (!this.canApproveInventoryCountsUi()) {
                this.$helper.ShowMessage('warning', 'غير مسموح', 'ليس لديك صلاحية إلغاء الجرد.');
                return;
            }
            Swal.fire({
                title: 'تأكيد إلغاء الجرد',
                input: 'textarea',
                inputLabel: 'سبب الإلغاء (اختياري)',
                inputPlaceholder: 'سبب الإلغاء...',
                icon: 'warning',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-danger me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد الإلغاء',
                cancelButtonText: 'تراجع',
            }).then((result) => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.CancelInventoryCountSession(this.detail.id, { Note: result.value || null })
                    .then((res) => {
                        this.$blockUI.Stop();
                        const msg = (res && res.data && (res.data.message || 'تم الإلغاء')) || 'تم الإلغاء';
                        this.$helper.ShowMessage('success', 'تم الإلغاء', msg);
                        this.LoadDetail(this.detail.id);
                    })
                    .catch((err) => {
                        this.$blockUI.Stop();
                        const msg = this.ExtractErrorMessage(err, 'تعذّر إلغاء الجرد.');
                        this.$helper.ShowMessage('error', 'فشل الإلغاء', msg);
                    });
            });
        },

        PrintSession() {
            if (!this.detail || !this.detail.id) return;
            const url = this.$http.GetInventoryCountPrintUrl(this.detail.id);
            const w = window.open(url, '_blank');
            if (!w) {
                this.$helper.ShowMessage('warning', 'النوافذ المنبثقة محجوبة', 'الرجاء السماح بالنوافذ المنبثقة لاستعراض تقرير الجرد.');
            }
        },

        // ─── Bootstrap Modal helpers ──────────────────────────────────────

        MoveBootstrapModalsToBody() {
            // فقط مودال إضافة صنف بقي على هذه الصفحة (لوحة مراجعة الفروقات أصبحت inline).
            const el = this.$refs.addLineModalEl;
            if (!el || el.parentElement === document.body) return;
            document.body.appendChild(el);
        },

        GetModalInstance(refName) {
            const el = this.$refs[refName];
            if (!el || !window.bootstrap || !window.bootstrap.Modal) return null;
            return window.bootstrap.Modal.getOrCreateInstance(el);
        },

        CloseModal(refName) {
            const inst = this.GetModalInstance(refName);
            if (inst) inst.hide();
        },

        // ─── Add Line modal ───────────────────────────────────────────────

        ResetAddLineForm() {
            this.addLineForm = {
                ProductsId: null,
                BatchNumber: '',
                ExpiryDate: null,
                PackageType: '',
                CountryOfOrigin: '',
                ActualQty: 1,
                Note: '',
            };
            this.addLineErrors = {};
            this.productSearchQuery = '';
            this.selectedProduct = null;
            this.selectedProductPackagings = [];
            this.addLineSubmitting = false;
            this.addLineProductLoading = false;
        },

        OpenAddLineDialog() {
            if (!this.detail || !this.detail.canSaveLines) {
                this.$helper.ShowMessage('warning', 'غير مسموح', 'لا يمكن إضافة أصناف لجلسة الجرد الحالية.');
                return;
            }
            this.ResetAddLineForm();
            this.$nextTick(() => {
                this.MoveBootstrapModalsToBody();
                this.LoadAddLineLookups();
                const inst = this.GetModalInstance('addLineModalEl');
                if (inst) inst.show();
            });
        },

        LoadAddLineLookups() {
            this.LoadProducts('');
            if (!this.countryOptions || this.countryOptions.length === 0) {
                this.lookupLoading.countries = true;
                this.$http.GetInventoryCountLookupCountries()
                    .then((res) => {
                        this.countryOptions = (res && res.data && res.data.info) || [];
                    })
                    .catch(() => { this.countryOptions = []; })
                    .finally(() => { this.lookupLoading.countries = false; });
            }
        },

        LoadProducts(search) {
            this.lookupLoading.products = true;
            this.$http.GetInventoryCountLookupProducts(search || '')
                .then((res) => {
                    this.productOptions = (res && res.data && res.data.info) || [];
                })
                .catch(() => { this.productOptions = []; })
                .finally(() => { this.lookupLoading.products = false; });
        },

        DebouncedSearchProducts() {
            const q = (this.productSearchQuery || '').trim();
            if (this.productSearchTimer) clearTimeout(this.productSearchTimer);
            this.productSearchTimer = setTimeout(() => {
                this.LoadProducts(q);
            }, 300);
        },

        OnProductChanged(productId) {
            if (this.addLineErrors.ProductsId) this.$set(this.addLineErrors, 'ProductsId', '');
            const id = Number(productId);
            this.addLineForm.PackageType = '';
            this.selectedProduct = null;
            this.selectedProductPackagings = [];
            if (!id) return;

            this.addLineProductLoading = true;
            this.$http.GetProductWithPackagings(id)
                .then((res) => {
                    const data = (res && res.data) || {};
                    this.selectedProduct = data.info || null;
                    this.selectedProductPackagings = data.packagings || [];

                    // Auto-fill country from product if not already set
                    if (this.selectedProduct && this.selectedProduct.countryRegionName) {
                        this.addLineForm.CountryOfOrigin = this.selectedProduct.countryRegionName;
                    }

                    // Auto-select the only packaging if there's exactly one
                    if (this.selectedProductPackagings.length === 1) {
                        this.addLineForm.PackageType = this.selectedProductPackagings[0].unitName;
                    }
                })
                .catch(() => {
                    this.selectedProduct = null;
                    this.selectedProductPackagings = [];
                })
                .finally(() => { this.addLineProductLoading = false; });
        },

        ValidateAddLineForm() {
            const errors = {};
            const f = this.addLineForm;
            const qty = Number(f.ActualQty);

            if (!f.ProductsId) errors.ProductsId = 'الرجاء اختيار المنتج.';
            if (!f.BatchNumber || !String(f.BatchNumber).trim()) errors.BatchNumber = 'رقم التشغيلة مطلوب.';
            if (!f.PackageType) errors.PackageType = 'الرجاء اختيار العبوة.';
            if (!f.CountryOfOrigin) errors.CountryOfOrigin = 'الرجاء اختيار بلد المنشأ.';
            if (!Number.isFinite(qty) || qty <= 0) errors.ActualQty = 'الكمية يجب أن تكون أكبر من صفر.';
            if (this.addLineRequiresExpiry && !f.ExpiryDate) {
                errors.ExpiryDate = 'تاريخ الصلاحية مطلوب لهذا المنتج.';
            }
            if (this.selectedProduct && this.selectedProductPackagings.length === 0) {
                errors.PackageType = 'هذا المنتج ليس له عبوات مسجلة.';
            }

            this.addLineErrors = errors;
            return Object.keys(errors).length === 0;
        },

        SubmitAddLine() {
            if (!this.detail || !this.detail.id) return;
            if (!this.ValidateAddLineForm()) return;

            const f = this.addLineForm;
            const payload = {
                ProductsId: Number(f.ProductsId),
                BatchNumber: String(f.BatchNumber || '').trim(),
                ExpiryDate: f.ExpiryDate || null,
                PackageType: String(f.PackageType || '').trim(),
                CountryOfOrigin: String(f.CountryOfOrigin || '').trim(),
                ActualQty: Number(f.ActualQty),
                Note: String(f.Note || '').trim() || null,
            };

            this.addLineSubmitting = true;
            this.$blockUI.Start();
            this.$http.AddInventoryCountLine(this.detail.id, payload)
                .then((res) => {
                    this.$blockUI.Stop();
                    this.addLineSubmitting = false;
                    const data = (res && res.data) || {};
                    this.$helper.ShowMessage('success', 'تمت الإضافة', data.message || 'تم إضافة الصنف.');
                    this.CloseModal('addLineModalEl');
                    this.LoadDetail(this.detail.id);
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.addLineSubmitting = false;
                    const msg = this.ExtractErrorMessage(err, 'تعذّر إضافة الصنف.');
                    this.ShowAboveModalError('فشل إضافة الصنف', msg);
                });
        },

        /**
         * يستخرج أوضح رسالة خطأ ممكنة من استجابة axios (يدعم string / object / ASP.NET ModelState).
         * كما يطبع التفاصيل الكاملة في console.error لتسهيل التشخيص أثناء التطوير.
         */
        ExtractErrorMessage(err, fallback) {
            try { console.error('[InventoryCountDetails] request failed:', err && err.response ? err.response : err); } catch (e) { /* */ }
            if (!err) return fallback || 'حدث خطأ غير متوقع.';
            const resp = err.response;
            if (resp) {
                const data = resp.data;
                if (typeof data === 'string' && data.trim()) return data.trim();
                if (data && typeof data === 'object') {
                    if (typeof data.message === 'string' && data.message) return data.message;
                    if (typeof data.error === 'string' && data.error) return data.error;
                    if (typeof data.title === 'string' && data.title) return data.title;
                    if (data.errors && typeof data.errors === 'object') {
                        const msgs = [];
                        Object.keys(data.errors).forEach((k) => {
                            const v = data.errors[k];
                            if (Array.isArray(v)) v.forEach((m) => { if (m) msgs.push(String(m)); });
                            else if (typeof v === 'string' && v) msgs.push(v);
                        });
                        if (msgs.length > 0) return msgs.join('<br/>');
                    }
                    try { return JSON.stringify(data); } catch (e) { /* */ }
                }
                if (resp.status && resp.statusText) return `[HTTP ${resp.status}] ${resp.statusText}`;
                if (resp.status) return `HTTP ${resp.status}`;
            }
            if (typeof err.message === 'string' && err.message) return err.message;
            return fallback || 'حدث خطأ غير متوقع.';
        },

        /**
         * يعرض SweetAlert فوق مودال Bootstrap عبر customClass.container — يضمن أن الـ z-index
         * أعلى من 1095 (z-index مودالاتنا)، فلا تظهر الرسالة "خلف" المودال.
         */
        ShowAboveModalError(title, html) {
            Swal.fire({
                icon: 'error',
                title: title || 'خطأ',
                html: html || 'حدث خطأ غير متوقع.',
                customClass: { container: 'swal-above-modal' },
                showCancelButton: false,
            });
        },
    },
};
